import { useEffect } from 'react';

/**
 * Injects a blob script and removes it on unmount.
 * Used for inline script content (not external URLs).
 */
const useScript = (scriptContent, condition = true) => {
  useEffect(() => {
    if (condition) {
      const blob = new Blob([scriptContent], { type: 'text/javascript' });
      const blobUrl = URL.createObjectURL(blob);
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = blobUrl;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
        URL.revokeObjectURL(blobUrl);
      };
    }
  }, [scriptContent, condition]);
};

/**
 * Module-level singleton state.
 *
 * loadedSrcs  — scripts that have fully loaded at least once.
 * pendingCbs  — queued onLoad callbacks while a script is in-flight.
 *
 * This guarantees that multiple consumers of the same src:
 *   1. Never inject duplicate <script> tags.
 *   2. All get their onLoad fired exactly once.
 *   3. If the script was already loaded on a previous mount, onLoad fires
 *      synchronously at effect time.
 */
const loadedSrcs = new Set();
const pendingCbs = {};

/**
 * useLoadScript
 *
 * Loads an external script exactly once per src per page lifetime.
 * Multiple simultaneous callers with the same src all receive their onLoad
 * callback when the single script tag fires its load event.
 *
 * @param {string} src - URL of the script to load.
 * @param {Object} [options]
 * @param {Function} [options.onLoad] - Fired once the script is ready.
 * @param {Function} [options.onDestroy] - Fired when the consumer unmounts.
 */
export const useLoadScript = (src, { onLoad, onDestroy } = {}) => {
  useEffect(() => {
    // Already fully loaded → fire immediately
    if (loadedSrcs.has(src)) {
      onLoad?.();
      return () => { onDestroy?.(); };
    }

    // Script is currently loading → queue this consumer's callback
    if (pendingCbs[src]) {
      pendingCbs[src].push(onLoad);
      return () => {
        onDestroy?.();
        // Remove from the queue if we unmount before the script loads
        if (pendingCbs[src]) {
          pendingCbs[src] = pendingCbs[src].filter((cb) => cb !== onLoad);
        }
      };
    }

    // First caller — create the script tag
    pendingCbs[src] = [onLoad];

    const script = document.createElement('script');
    script.async = true;
    script.src = src;

    script.onload = () => {
      loadedSrcs.add(src);
      const callbacks = pendingCbs[src] || [];
      delete pendingCbs[src];
      callbacks.forEach((cb) => cb?.());
    };

    script.onerror = () => {
      // Allow a retry by removing the pending state.
      // Consumers that queued onLoad will not receive it — they should
      // surface an error via their own state (e.g. isScriptLoaded stays false).
      const callbacks = pendingCbs[src] || [];
      delete pendingCbs[src];
      // Remove the failed tag so a future mount can retry
      if (script.parentNode) script.parentNode.removeChild(script);
      // Notify all queued consumers of the failure via a null call so they
      // can detect the script never loaded (onLoad won't be called).
      callbacks.forEach((cb) => {
        if (typeof cb === 'function' && cb.onError) cb.onError();
      });
    };

    document.head.appendChild(script);

    return () => {
      onDestroy?.();
      // Do NOT remove the script tag on unmount — once loaded it is global
      // and other consumers depend on window.SpreedlyHostedFields.
      if (pendingCbs[src]) {
        pendingCbs[src] = pendingCbs[src].filter((cb) => cb !== onLoad);
      }
    };
  }, [src]);
};

export default useScript;
